import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUsadoContext } from '@/shared/api/controllers/ventas';
import { AlertTriangle, Check, RefreshCw, Search, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { listComprobantesByOperacion } from '@/shared/api/controllers/comprobantePagos';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { etiquetaCaja } from '@/shared/lib/cajasEfectivo';
import type { AutorizacionEliminacionItem } from '@/shared/api/controllers/autorizacionEliminaciones';
import {
  aprobarCambioHorno,
  aprobarDescuento,
  previewDiscountUsado,
  aprobarEliminacion,
  aprobarFacturacionPrevia,
  aprobarFacturaPorcentaje,
  listPendingCambiosHorno,
  listPendingDescuentos,
  listPendingEliminaciones,
  listPendingFacturacionPrevia,
  listPendingFacturaPorcentaje,
  rechazarCambioHorno,
  rechazarDescuento,
  rechazarEliminacion,
  rechazarFacturacionPrevia,
  rechazarFacturaPorcentaje,
  type AutorizacionDescuentosItemWithSummary,
  type CambioHornoItem,
  type FacturacionPreviaItem,
  type FacturaPorcentajeItem,
} from './api';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';
import { listEcheqs, type Echeq } from '@/shared/api/controllers/echeqs';
import {
  AutorizarEcheqDialog,
  fechaAr,
  RechazarEcheqDialog,
} from '@/features/administracion/components/EcheqDialogs';

// Inventario approvals were a PowerApps-era queue; the React app never
// creates new ones, and the legacy backfill brought in a small handful
// of pending rows that admins can ignore. The tab is hidden here but
// the API wrappers in `./api.ts` stay intact in case the workflow is
// ever re-enabled (set Mode union back to include 'Inventario').
type Mode =
  | 'Eliminaciones'
  | 'Descuentos'
  | 'FacturaPorcentaje'
  | 'CambioHorno'
  | 'FacturacionPrevia'
  | 'Echeqs';

type DialogState =
  | { kind: 'closed' }
  | { kind: 'aprobar-eliminacion'; row: AutorizacionEliminacionItem }
  | { kind: 'rechazar-eliminacion'; row: AutorizacionEliminacionItem }
  | { kind: 'aprobar-descuento'; row: AutorizacionDescuentosItemWithSummary }
  | {
      kind: 'rechazar-descuento';
      row: AutorizacionDescuentosItemWithSummary;
      /** Pre-fill the % field when arriving from the overpayment-risk
       *  fork inside the approve dialog. */
      initialValue?: number;
    }
  | { kind: 'aprobar-factura-pct'; row: FacturaPorcentajeItem }
  | { kind: 'rechazar-factura-pct'; row: FacturaPorcentajeItem }
  | { kind: 'aprobar-cambio-horno'; row: CambioHornoItem }
  | { kind: 'rechazar-cambio-horno'; row: CambioHornoItem }
  | { kind: 'aprobar-facturacion-previa'; row: FacturacionPreviaItem }
  | { kind: 'rechazar-facturacion-previa'; row: FacturacionPreviaItem }
  | { kind: 'autorizar-echeq'; row: Echeq }
  | { kind: 'rechazar-echeq'; row: Echeq };

/**
 * Aprobaciones — admin/administración queue for AutorizacionEliminaciones
 * and AutorizacionDescuentos. Ported from `Screen_Aprobaciones.pa.yaml`.
 *
 * Layout mirrors ContactosPage exactly: outer `flex h-full flex-col`,
 * toolbar is `shrink-0`, the table is a `min-h-0 flex-1 overflow-y-auto`
 * scroller with a sticky header.
 *
 * Pagos confirmation is explicitly NOT handled here — that's the
 * Administración module, ported next.
 */
export function AprobacionesPage() {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const [mode, setMode] = useState<Mode>('Eliminaciones');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });

  // Approval queues — refetch on refocus + every 15s while mounted so
  // approvers see new requests as they come in and don't action a row
  // someone else just resolved. Paired with the OCC guards on the
  // mutation endpoints.
  const REFRESH_MS = 15_000;
  // Country code in every queryKey — approvals are country-scoped on
  // the server and cross-country admins switching mid-session must
  // see fresh data.
  const countryCode = activeCountry.code;
  const eliminacionesQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'eliminaciones'],
    queryFn: listPendingEliminaciones,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const descuentosQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'descuentos'],
    queryFn: listPendingDescuentos,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const facturaPctQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'factura-pct'],
    queryFn: listPendingFacturaPorcentaje,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const cambioHornoQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'cambio-horno'],
    queryFn: listPendingCambiosHorno,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const facturacionPreviaQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'facturacion-previa'],
    queryFn: listPendingFacturacionPrevia,
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const echeqsQ = useQuery({
    queryKey: ['aprobaciones', countryCode, 'echeqs'],
    queryFn: () => listEcheqs({ estado: ['Pendiente Autorizacion'] }),
    enabled: countryCode === 'AR',
    staleTime: 30_000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });

  // Live updates across both approval queues — admins see new requests
  // as soon as the requestor submits, and the requestor's other tabs
  // see resolution flips instantly. (Inventario channel intentionally
  // omitted — the queue is hidden; see Mode comment above.)
  useRealtimeInvalidate(
    [
      'deletion_authorizations',
      'discount_authorizations',
      'factura_percentage_authorizations',
      'article_swap_authorizations',
      'facturacion_previa_authorizations',
      'echeqs',
    ],
    ['aprobaciones'],
  );

  const isFetching =
    (mode === 'Eliminaciones' && eliminacionesQ.isFetching) ||
    (mode === 'Descuentos' && descuentosQ.isFetching) ||
    (mode === 'FacturaPorcentaje' && facturaPctQ.isFetching) ||
    (mode === 'CambioHorno' && cambioHornoQ.isFetching) ||
    (mode === 'FacturacionPrevia' && facturacionPreviaQ.isFetching) ||
    (mode === 'Echeqs' && echeqsQ.isFetching);
  const handleRefresh = () => {
    qc.invalidateQueries({ queryKey: ['aprobaciones'] });
  };

  const filteredEliminaciones = useMemo(() => {
    const rows = eliminacionesQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const f = r.fields;
      return (
        (f.Cliente_AE ?? '').toLowerCase().includes(q) ||
        (f.Usuario_AE ?? '').toLowerCase().includes(q) ||
        (f.IDOperacion_AE ?? '').toLowerCase().includes(q)
      );
    });
  }, [eliminacionesQ.data, search]);

  const filteredDescuentos = useMemo(() => {
    const rows = descuentosQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const f = r.fields;
      return (
        (f.Articulo_AD ?? '').toLowerCase().includes(q) ||
        (f.IDOperacion_AD ?? '').toLowerCase().includes(q) ||
        (f.Motivo_AD ?? '').toLowerCase().includes(q)
      );
    });
  }, [descuentosQ.data, search]);

  const filteredFacturaPct = useMemo(() => {
    const rows = facturaPctQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.venta?.cliente ?? '').toLowerCase().includes(q) ||
        String(r.venta?.sharepointId ?? '').toLowerCase().includes(q) ||
        (r.solicitanteUsuarioApp ?? '').toLowerCase().includes(q) ||
        (r.motivo ?? '').toLowerCase().includes(q)
      );
    });
  }, [facturaPctQ.data, search]);

  const filteredCambiosHorno = useMemo(() => {
    const rows = cambioHornoQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.venta?.cliente ?? '').toLowerCase().includes(q) ||
        String(r.venta?.sharepointId ?? '').toLowerCase().includes(q) ||
        (r.hornoAnterior ?? '').toLowerCase().includes(q) ||
        (r.hornoNuevo ?? '').toLowerCase().includes(q) ||
        (r.solicitanteUsuarioApp ?? '').toLowerCase().includes(q)
      );
    });
  }, [cambioHornoQ.data, search]);

  const filteredFacturacionPrevia = useMemo(() => {
    const rows = facturacionPreviaQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.venta?.cliente ?? '').toLowerCase().includes(q) ||
        String(r.venta?.sharepointId ?? '').toLowerCase().includes(q) ||
        (r.venta?.nroCotizacion ?? '').toLowerCase().includes(q) ||
        (r.comprobante?.medioPago ?? '').toLowerCase().includes(q) ||
        (r.solicitanteUsuarioApp ?? '').toLowerCase().includes(q) ||
        (r.motivo ?? '').toLowerCase().includes(q)
      );
    });
  }, [facturacionPreviaQ.data, search]);

  const filteredEcheqs = useMemo(() => {
    const rows = echeqsQ.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.cliente, r.ventaId, r.nroCheque, r.bancoEmisor, r.cuitLibrador, r.solicitante].some((v) =>
        (v ?? '').toLowerCase().includes(q),
      ),
    );
  }, [echeqsQ.data, search]);

  return (
    <div className="flex h-full flex-col gap-6">
      {/* Toolbar — DOM order matches desktop (Title, Mode Select,
          Search, Refresh). On mobile: title row gets [Title · Refresh]
          via `order`, then a `basis-full` break drops Mode Select +
          Search onto a second row. */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Aprobaciones
        </h1>
        <div className="order-3 w-36 shrink-0 sm:order-none sm:w-52">
          <Select
            value={mode}
            onValueChange={(v) => {
              setMode(v as Mode);
              // Close any open dialog when the queue changes — otherwise
              // an aprobar-eliminacion dialog stays mounted after the
              // user switches to FacturaPorcentaje, which looks broken.
              setDialog({ kind: 'closed' });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Eliminaciones">Eliminaciones</SelectItem>
              <SelectItem value="Descuentos">Descuentos</SelectItem>
              <SelectItem value="FacturaPorcentaje">Facturación %</SelectItem>
              <SelectItem value="CambioHorno">Cambio de horno</SelectItem>
              {/* Facturación Previa es AR-only: en MX la factura es CFDI y
                  esta cola no existe. */}
              {countryCode === 'AR' && (
                <SelectItem value="FacturacionPrevia">Factura Anticipada</SelectItem>
              )}
              {countryCode === 'AR' && <SelectItem value="Echeqs">E-cheqs</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        {/* Forces select + search onto a new row on mobile only. */}
        <div className="order-2 basis-full sm:hidden" aria-hidden />
        <div className="relative order-4 min-w-[10rem] flex-1 sm:order-none sm:w-72 sm:flex-none">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar solicitud"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={handleRefresh}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw
            className={cn('h-4 w-4', isFetching && 'animate-spin')}
            aria-hidden
          />
        </Button>
      </div>

      {mode === 'Eliminaciones' && (
        <EliminacionesTable
          rows={filteredEliminaciones}
          loading={eliminacionesQ.isPending}
          error={eliminacionesQ.isError}
          searchActive={Boolean(search.trim())}
          onApprove={(row) => setDialog({ kind: 'aprobar-eliminacion', row })}
          onReject={(row) => setDialog({ kind: 'rechazar-eliminacion', row })}
        />
      )}
      {mode === 'Descuentos' && (
        <DescuentosTable
          rows={filteredDescuentos}
          loading={descuentosQ.isPending}
          error={descuentosQ.isError}
          searchActive={Boolean(search.trim())}
          onApprove={(row) => setDialog({ kind: 'aprobar-descuento', row })}
          onReject={(row) => setDialog({ kind: 'rechazar-descuento', row })}
        />
      )}
      {mode === 'FacturaPorcentaje' && (
        <FacturaPorcentajeTable
          rows={filteredFacturaPct}
          loading={facturaPctQ.isPending}
          error={facturaPctQ.isError}
          searchActive={Boolean(search.trim())}
          onApprove={(row) => setDialog({ kind: 'aprobar-factura-pct', row })}
          onReject={(row) => setDialog({ kind: 'rechazar-factura-pct', row })}
        />
      )}
      {mode === 'CambioHorno' && (
        <CambioHornoTable
          rows={filteredCambiosHorno}
          loading={cambioHornoQ.isPending}
          error={cambioHornoQ.isError}
          searchActive={Boolean(search.trim())}
          onApprove={(row) => setDialog({ kind: 'aprobar-cambio-horno', row })}
          onReject={(row) => setDialog({ kind: 'rechazar-cambio-horno', row })}
        />
      )}
      {mode === 'FacturacionPrevia' && (
        <FacturacionPreviaTable
          rows={filteredFacturacionPrevia}
          loading={facturacionPreviaQ.isPending}
          error={facturacionPreviaQ.isError}
          searchActive={Boolean(search.trim())}
          onApprove={(row) => setDialog({ kind: 'aprobar-facturacion-previa', row })}
          onReject={(row) => setDialog({ kind: 'rechazar-facturacion-previa', row })}
        />
      )}

      {mode === 'Echeqs' && (
        <EcheqsTable
          rows={filteredEcheqs}
          loading={echeqsQ.isPending}
          error={echeqsQ.isError}
          searchActive={Boolean(search.trim())}
          onApprove={(row) => setDialog({ kind: 'autorizar-echeq', row })}
          onReject={(row) => setDialog({ kind: 'rechazar-echeq', row })}
        />
      )}
      {dialog.kind === 'autorizar-echeq' && (
        <AutorizarEcheqDialog echeq={dialog.row} onClose={() => setDialog({ kind: 'closed' })} />
      )}
      {dialog.kind === 'rechazar-echeq' && (
        <RechazarEcheqDialog
          echeq={dialog.row}
          modo="autorizacion"
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}

      {dialog.kind === 'aprobar-eliminacion' && (
        <AprobarEliminacionDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'rechazar-eliminacion' && (
        <RechazarEliminacionDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'aprobar-descuento' && (
        <AprobarDescuentoDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
          onOpenReject={(initialValue) => {
            // Hand the same row over to the reject dialog, seeded
            // with the server's suggested max %.
            setDialog({
              kind: 'rechazar-descuento',
              row: dialog.row,
              initialValue: initialValue ?? undefined,
            });
          }}
        />
      )}
      {dialog.kind === 'rechazar-descuento' && (
        <RechazarDescuentoDialog
          row={dialog.row}
          initialValue={dialog.initialValue}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'aprobar-factura-pct' && (
        <AprobarFacturaPctDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'rechazar-factura-pct' && (
        <RechazarFacturaPctDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'aprobar-cambio-horno' && (
        <AprobarCambioHornoDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'rechazar-cambio-horno' && (
        <RechazarCambioHornoDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'aprobar-facturacion-previa' && (
        <AprobarFacturacionPreviaDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
      {dialog.kind === 'rechazar-facturacion-previa' && (
        <RechazarFacturacionPreviaDialog
          row={dialog.row}
          onClose={() => setDialog({ kind: 'closed' })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

function EliminacionesTable({
  rows,
  loading,
  error,
  searchActive,
  onApprove,
  onReject,
}: {
  rows: AutorizacionEliminacionItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onApprove: (row: AutorizacionEliminacionItem) => void;
  onReject: (row: AutorizacionEliminacionItem) => void;
}) {
  const { config: activeCountry } = useActiveCountry();
  const rowCurrency: 'ARS' | 'MXN' = activeCountry.code === 'MX' ? 'MXN' : 'ARS';
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay solicitudes de eliminación pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>ID Operación</Th>
              <Th>Cliente</Th>
              <Th className="text-right">Monto</Th>
              <Th>Usuario</Th>
              <Th className="w-32 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={6} />
            ) : error ? (
              <RowStatus cols={6}>
                <span className="text-destructive">
                  No se pudieron cargar las solicitudes.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={6}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const f = r.fields;
                const monto = parseMoney(f.Monto_AE);
                return (
                  <tr key={r.id} className="transition-colors hover:bg-muted/30">
                    <Td className="text-muted-foreground">{f.Fecha_AE || '—'}</Td>
                    <Td className="text-foreground">
                      <div>{f.IDOperacion_AE || '—'}</div>
                      <AlcanceEliminacion f={f} />
                    </Td>
                    <Td className="font-medium text-foreground">{f.Cliente_AE || '—'}</Td>
                    <Td className="text-right font-medium text-foreground">
                      {monto != null ? formatMoney(monto, rowCurrency) : '—'}
                    </Td>
                    <Td className="text-muted-foreground">{f.Usuario_AE || '—'}</Td>
                    <Td className="text-right">
                      <ActionCell
                        onApprove={() => onApprove(r)}
                        onReject={() => onReject(r)}
                      />
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
        >
          {rows.map((r) => {
            const f = r.fields;
            const monto = parseMoney(f.Monto_AE);
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-medium text-foreground">
                    {f.Cliente_AE || '—'}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {f.Fecha_AE || '—'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Operación {f.IDOperacion_AE || '—'} · {f.Usuario_AE || '—'}
                </div>
                <AlcanceEliminacion f={f} />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-semibold text-foreground">
                    {monto != null ? formatMoney(monto, rowCurrency) : '—'}
                  </span>
                  <ActionCell onApprove={() => onApprove(r)} onReject={() => onReject(r)} />
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

/**
 * Qué se anula al aprobar: la venta entera, o sólo un cobro con factura
 * emitida ("Solicitar anulación" desde Pagos).
 */
function AlcanceEliminacion({ f }: { f: AutorizacionEliminacionItem['fields'] }) {
  if (!f.IDComprobante_AE) {
    return <div className="text-xs text-muted-foreground">Eliminar venta</div>;
  }
  return (
    <div className="text-xs">
      <span className="rounded-md bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">
        Anular cobro
      </span>{' '}
      <span className="text-muted-foreground">
        {[f.MedioPago_AE, f.NroFactura_AE && `Factura ${f.NroFactura_AE}`]
          .filter(Boolean)
          .join(' · ')}
      </span>
    </div>
  );
}

function DescuentosTable({
  rows,
  loading,
  error,
  searchActive,
  onApprove,
  onReject,
}: {
  rows: AutorizacionDescuentosItemWithSummary[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onApprove: (row: AutorizacionDescuentosItemWithSummary) => void;
  onReject: (row: AutorizacionDescuentosItemWithSummary) => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay solicitudes de descuento pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>ID Operación</Th>
              <Th>Artículo</Th>
              <Th className="text-right">Descuento</Th>
              {/* Pagado / Proyectado — the financial preview lives next
                  to the action so the approver can sanity-check before
                  clicking. Cell highlights amber when approving would
                  put the venta into overpayment territory. */}
              <Th className="text-right">Pagado / Proyectado</Th>
              <Th>Motivo</Th>
              <Th className="w-32 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={7} />
            ) : error ? (
              <RowStatus cols={7}>
                <span className="text-destructive">
                  No se pudieron cargar las solicitudes.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={7}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const f = r.fields;
                const s = r.summary;
                return (
                  <tr key={r.id} className={cn(
                    'transition-colors',
                    f.WasUsado_AD
                      ? 'bg-rose-50/60 hover:bg-rose-100/60'
                      : 'hover:bg-muted/30',
                  )}>
                    <Td className="text-muted-foreground">{f.Fecha_AD || '—'}</Td>
                    <Td className="text-foreground">{f.IDOperacion_AD || '—'}</Td>
                    <Td className="font-medium text-foreground">
                      <div className="inline-flex items-center gap-1.5">
                        <span>{f.Articulo_AD || '—'}</span>
                        {f.WasUsado_AD && (
                          <span className="shrink-0 rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                            Usado
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="text-right font-semibold text-foreground">
                      {f.Descuento_AD ? `${f.Descuento_AD}%` : '—'}
                    </Td>
                    <Td className="text-right">
                      {s ? (
                        <div
                          className={cn(
                            'flex flex-col items-end gap-0.5 leading-tight tabular-nums',
                            s.overpaymentRisk && 'text-amber-700',
                          )}
                          title={`Total actual: ${formatMoney(s.montoActualTotal, s.moneda)}`}
                        >
                          <span className="text-xs text-muted-foreground">
                            Pag. {formatMoney(s.montoPagado, s.moneda)}
                          </span>
                          <span
                            className={cn(
                              'text-sm font-semibold',
                              s.overpaymentRisk ? 'text-amber-800' : 'text-foreground',
                            )}
                          >
                            {formatMoney(s.montoProyectadoTotal, s.moneda)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                    <Td
                      className="max-w-xs truncate text-muted-foreground"
                      title={f.Motivo_AD ?? undefined}
                    >
                      {f.Motivo_AD || '—'}
                    </Td>
                    <Td className="text-right">
                      <ActionCell
                        onApprove={() => onApprove(r)}
                        onReject={() => onReject(r)}
                      />
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
        >
          {rows.map((r) => {
            const f = r.fields;
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-foreground">
                      {f.Articulo_AD || '—'}
                    </span>
                    {f.WasUsado_AD && (
                      <span className="shrink-0 rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                        Usado
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 rounded-md bg-secondary px-2 py-0.5 text-[11px] font-semibold text-foreground">
                    {f.Descuento_AD ? `${f.Descuento_AD}%` : '—'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {f.Fecha_AD || '—'} · Operación {f.IDOperacion_AD || '—'}
                </div>
                {f.Motivo_AD && (
                  <div className="text-xs text-muted-foreground">
                    Motivo: <span className="text-foreground">{f.Motivo_AD}</span>
                  </div>
                )}
                <div className="flex justify-end">
                  <ActionCell onApprove={() => onApprove(r)} onReject={() => onReject(r)} />
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function AprobarEliminacionDialog({
  row,
  onClose,
}: {
  row: AutorizacionEliminacionItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const isMx = activeCountry.code === 'MX';
  const f = row.fields;
  const monto = parseMoney(f.Monto_AE);
  // Este es el camino MÁS destructivo de todos: anula todos los comprobantes
  // no terminales de la venta Y revierte el asiento de caja. Con Facturación
  // Previa alguno de esos comprobantes puede tener factura emitida, y quien
  // aprueba acá no tiene forma de saberlo — la solicitud sólo trae venta,
  // cliente y monto. Lookup on-demand, sólo con el diálogo abierto.
  const facturadasQ = useQuery({
    queryKey: ['pagos', 'comprobantes-full', String(f.IDOperacion_AE ?? '')],
    queryFn: () => listComprobantesByOperacion(f.IDOperacion_AE!),
    enabled: Boolean(f.IDOperacion_AE),
    staleTime: 30_000,
  });
  // Solicitud de un cobro ("Solicitar anulación"): se anula sólo ese cobro.
  const esCobro = Boolean(f.IDComprobante_AE);
  const facturasVivas = (facturadasQ.data ?? []).filter(
    (c) =>
      c.fields.Facturado_CP === 'SI' &&
      (c.fields.Status_P ?? '') !== 'Anulado' &&
      (!esCobro || c.id === f.IDComprobante_AE),
  );
  const mutation = useMutation({
    mutationFn: () => aprobarEliminacion(row.id),
    onSuccess: (r) => {
      if (r.outcome === 'approved') {
        if (r.alcance === 'cobro') {
          toast.success('Anulación aprobada', {
            description: `Se anuló el cobro${r.nroFactura ? ` de la factura ${r.nroFactura}` : ''} de la venta Nº ${r.operacionId}.`,
          });
        } else {
          toast.success('Eliminación aprobada', {
            description: `Venta Nº ${r.operacionId} anulada junto a ${r.anulados.detalles} detalles, ${r.anulados.pagos} pagos y ${r.anulados.comprobantes} comprobantes.`,
          });
        }
        if (facturasVivas.length > 0) {
          toast.warning('Falta la Nota de Crédito', {
            description:
              'Había facturas emitidas: siguen vigentes en ARCA hasta que se emita la Nota de Crédito. Quedan en el aviso de la pantalla principal.',
          });
        }
      } else {
        toast.info('Solicitud no aplicable', { description: r.message });
      }
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      // `['home','operaciones']` NO prefix-matchea `['home', countryCode,
      // 'operaciones']` (HomePage:108) — element[1] difiere, así que esa
      // invalidación nunca hizo nada. El prefijo `['home']` sí.
      qc.invalidateQueries({ queryKey: ['home'] });
      // La venta anulada puede dejar facturas vivas ante ARCA: refrescar el
      // aviso de facturas sin venta activa.
      qc.invalidateQueries({ queryKey: ['facturacion'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo aprobar la eliminación', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });
  const processingMessage = esCobro ? 'Aprobando anulación…' : 'Aprobando eliminación…';

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && processingMessage}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>{esCobro ? 'Aprobar anulación de cobro' : 'Aprobar eliminación'}</DialogTitle>
              <DialogDescription>
                {esCobro ? (
                  <>
                    Se anulará <strong>sólo el cobro</strong>
                    {f.MedioPago_AE ? ` en ${f.MedioPago_AE}` : ''}
                    {f.NroFactura_AE ? ` (factura ${f.NroFactura_AE})` : ''} de la venta Nº{' '}
                    {f.IDOperacion_AE} de {f.Cliente_AE || 'cliente'}
                    {monto != null ? ` por ${formatMoney(monto, isMx ? 'MXN' : 'ARS')}` : ''}. La
                    venta sigue activa.
                  </>
                ) : (
                  <>
                Se anulará la venta Nº {f.IDOperacion_AE} de {f.Cliente_AE || 'cliente'}
                {monto != null ? ` por ${formatMoney(monto, isMx ? 'MXN' : 'ARS')}` : ''},
                junto con sus detalles, descuentos, pagos y comprobantes.
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          {!esCobro && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {isMx
              ? 'Los ajustes de caja (reversos de IVA, comisiones Nova, Caja Diferencia) y el stock se procesarán desde Tesorería — esta acción solo anula las filas de la venta.'
              : 'Los ajustes de caja (reversos de SIRCREB, IVA, etc.) y el stock se procesarán desde Tesorería — esta acción solo anula las filas de la venta.'}
          </p>
          )}
          {facturasVivas.length > 0 && (
            <div className="mt-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
              <p className="font-semibold uppercase tracking-wide text-rose-700">
                {esCobro
                  ? 'Este cobro tiene factura emitida'
                  : facturasVivas.length === 1
                    ? 'Esta venta tiene una factura emitida'
                    : `Esta venta tiene ${facturasVivas.length} facturas emitidas`}
              </p>
              <p className="mt-1">
                {facturasVivas
                  .map((c) => c.fields.NroFactura_CP)
                  .filter(Boolean)
                  .join(', ')}
                {facturasVivas.some((c) => c.fields.NroFactura_CP) ? '. ' : ''}
                Aprobar anula esos comprobantes, pero{' '}
                <strong>no da de baja las facturas</strong>: siguen vigentes
                ante ARCA y sólo una <strong>Nota de Crédito</strong> las
                revierte. Al aprobar, quedan en el aviso amarillo de la pantalla
                principal para que Administración emita la Nota de Crédito antes
                del fin del mes en que se emitió cada una.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="bg-rose-600 text-white hover:bg-rose-700"
          >
            {esCobro ? 'Aprobar anulación' : 'Confirmar eliminación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RechazarEliminacionDialog({
  row,
  onClose,
}: {
  row: AutorizacionEliminacionItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const f = row.fields;
  const mutation = useMutation({
    mutationFn: () => rechazarEliminacion(row.id),
    onSuccess: (r) => {
      if (r.outcome === 'rejected') toast.success('Solicitud rechazada');
      else toast.info('Solicitud no aplicable', { description: r.message });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo rechazar la solicitud', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });
  const processingMessage = 'Rechazando solicitud…';

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && processingMessage}
      >
        <DialogHeader>
          <DialogTitle>{f.IDComprobante_AE ? 'Rechazar anulación de cobro' : 'Rechazar eliminación'}</DialogTitle>
          <DialogDescription>
            {f.IDComprobante_AE
              ? `Se rechazará la solicitud de anular el cobro${f.NroFactura_AE ? ` de la factura ${f.NroFactura_AE}` : ''} de la venta Nº ${f.IDOperacion_AE}. El cobro sigue pendiente.`
              : `Se rechazará la solicitud de eliminación para la venta Nº ${f.IDOperacion_AE}. La venta permanecerá activa.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Rechazar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AprobarDescuentoDialog({
  row,
  onClose,
  onOpenReject,
}: {
  row: AutorizacionDescuentosItemWithSummary;
  onClose: () => void;
  /** Called when the admin picks "Rechazar con otro %" on the
   *  overpayment-risk fork. The page swaps this dialog out for
   *  RechazarDescuentoDialog with the suggested ceiling pre-filled. */
  onOpenReject: (initialValue: number | null) => void;
}) {
  const qc = useQueryClient();
  const f = row.fields;
  // Local copy of the server's overpayment-risk payload — populated
  // when the mutation lands on the `overpayment_risk` outcome. While
  // set we render the forked UI (cancel-vs-reject) instead of the
  // standard Aprobar button.
  const [blocked, setBlocked] = useState<{
    currentTotal: number;
    projectedTotal: number;
    paidSum: number;
    proposedDiscountPct: number;
    suggestedMaxPct: number | null;
    message: string;
  } | null>(null);
  const mutation = useMutation({
    mutationFn: () => aprobarDescuento(row.id),
    onSuccess: (r) => {
      if (r.outcome === 'approved') {
        toast.success('Descuento aprobado');
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
        qc.invalidateQueries({ queryKey: ['ventas'] });
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        qc.invalidateQueries({ queryKey: ['home', 'operaciones'] });
        onClose();
        return;
      }
      if (r.outcome === 'overpayment_risk') {
        // Stay in the dialog — switch into the forked UI so the admin
        // picks an alternative (reject with smaller % OR cancel the
        // request entirely) instead of letting the approval through.
        setBlocked({
          currentTotal: r.currentTotal,
          projectedTotal: r.projectedTotal,
          paidSum: r.paidSum,
          proposedDiscountPct: r.proposedDiscountPct,
          suggestedMaxPct: r.suggestedMaxPct,
          message: r.message,
        });
        return;
      }
      // blocked_status / other no-op outcomes
      toast.info('Solicitud no aplicable', { description: r.message });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo aprobar el descuento', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });
  // "Cancelar solicitud" — rejects the autorización with 0% replacement,
  // which clears the pending state without touching the venta total
  // (pending lines were already at descuento_pct=0 in our two-state
  // model, so rejecting with 0% is a clean no-op on the math).
  const cancelMutation = useMutation({
    mutationFn: () => rechazarDescuento(row.id, 0),
    onSuccess: () => {
      toast.success('Solicitud cancelada', {
        description: 'El descuento pendiente se quitó de la venta.',
      });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      qc.invalidateQueries({ queryKey: ['detalle-venta'] });
      qc.invalidateQueries({ queryKey: ['home', 'operaciones'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo cancelar la solicitud', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });
  const processingMessage = cancelMutation.isPending
    ? 'Cancelando solicitud…'
    : 'Aprobando descuento…';
  const isPending = mutation.isPending || cancelMutation.isPending;

  // When the server has blocked the approval (overpayment_risk), the
  // dialog reads from `blocked` instead of `row.summary`. The two
  // share the same shape conceptually; we render unified panels and
  // a forked footer.
  const moneda = row.summary?.moneda ?? 'ARS';
  const breakdown = blocked
    ? {
        montoPagado: blocked.paidSum,
        montoActualTotal: blocked.currentTotal,
        montoProyectadoTotal: blocked.projectedTotal,
        moneda,
      }
    : row.summary;

  return (
    <Dialog open onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={isPending && processingMessage}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              className={cn(
                'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                blocked
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-emerald-100 text-emerald-700',
              )}
            >
              {blocked ? (
                <AlertTriangle className="h-5 w-5" aria-hidden />
              ) : (
                <ShieldCheck className="h-5 w-5" aria-hidden />
              )}
            </span>
            <div>
              <DialogTitle>
                {blocked ? 'No se puede aprobar' : 'Aprobar descuento'}
              </DialogTitle>
              <DialogDescription>
                {blocked
                  ? `El descuento del ${blocked.proposedDiscountPct}% solicitado sobre ${f.Articulo_AD} dejaría la venta en sobre-pago.`
                  : (
                    <>
                      Se aprobará el descuento del {f.Descuento_AD}% solicitado sobre{' '}
                      <span className="font-medium text-foreground">{f.Articulo_AD}</span>.
                    </>
                  )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {breakdown && (
          <DialogBody className="space-y-2">
            {f.WasUsado_AD && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
                <p className="font-semibold uppercase tracking-wide text-rose-700">
                  ⚠ Usado
                </p>
                <p className="mt-1">
                  Con este descuento la venta queda por debajo del costo
                  contable + comisión + IVA. Si aprobás, la{' '}
                  <strong>factura interna</strong> de esta venta saldrá
                  marcada como usado: administración tendrá que cargar el
                  monto manualmente al subir el PDF (el sistema no
                  valida la coincidencia con el costo contable). El
                  margen que se transfiere a Caja Diferencia depende de
                  ese monto: si es igual al cobrado, no hay margen para
                  transferir; si es menor, se transfiere la diferencia.
                </p>
              </div>
            )}
            {/* Paid / current / projected breakdown — same shape in
                both the preview state and the blocked state. The
                blocked branch tweaks the colors below + adds a forked
                footer with cancel/reject alternatives. */}
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <SummaryRow
                label="Pagado"
                value={formatMoney(breakdown.montoPagado, breakdown.moneda)}
              />
              <SummaryRow
                label="Total actual"
                value={formatMoney(breakdown.montoActualTotal, breakdown.moneda)}
              />
              <div className="mt-1 border-t pt-1.5">
                <SummaryRow
                  label="Total proyectado"
                  value={formatMoney(breakdown.montoProyectadoTotal, breakdown.moneda)}
                  emphasize
                />
              </div>
            </div>
            {blocked && (
              <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <AlertTriangle
                  className="mt-0.5 h-3.5 w-3.5 shrink-0"
                  aria-hidden
                />
                <span>
                  El pagado supera el total proyectado por{' '}
                  <span className="font-semibold">
                    {formatMoney(
                      blocked.paidSum - blocked.projectedTotal,
                      breakdown.moneda,
                    )}
                  </span>
                  .{' '}
                  {blocked.suggestedMaxPct != null
                    ? `Probá con un descuento máximo del ${blocked.suggestedMaxPct}% o cancelá la solicitud.`
                    : 'Incluso sin descuento la venta queda en sobre-pago — cancelá la solicitud y revisá los cobros.'}
                </span>
              </p>
            )}
          </DialogBody>
        )}
        <DialogFooter>
          {blocked ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isPending}
              >
                Cerrar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => cancelMutation.mutate()}
                disabled={isPending}
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
              >
                Cancelar solicitud
              </Button>
              {blocked.suggestedMaxPct != null && (
                <Button
                  type="button"
                  onClick={() => {
                    const seed = blocked.suggestedMaxPct;
                    onClose();
                    onOpenReject(seed);
                  }}
                  disabled={isPending}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  Rechazar con {blocked.suggestedMaxPct}%
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={isPending}
              >
                Aprobar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span
        className={cn(
          emphasize ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums',
          emphasize ? 'font-semibold text-foreground' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function RechazarDescuentoDialog({
  row,
  initialValue,
  onClose,
}: {
  row: AutorizacionDescuentosItemWithSummary;
  /** Pre-fill the % field. Used when this dialog opens as a follow-up
   *  to the overpayment-risk fork inside AprobarDescuentoDialog — we
   *  seed the input with `suggestedMaxPct` so the admin only has to
   *  confirm. */
  initialValue?: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const f = row.fields;
  const original = Number((f.Descuento_AD ?? '0').replace(',', '.'));
  const [value, setValue] = useState(
    initialValue != null ? String(initialValue) : '',
  );
  // Server-returned overpayment block. Surfaced inline so the admin
  // can adjust the % and re-submit without bouncing through another
  // dialog. Cleared whenever the input changes.
  const [blocked, setBlocked] = useState<{
    currentTotal: number;
    projectedTotal: number;
    paidSum: number;
    proposedDiscountPct: number;
    suggestedMaxPct: number | null;
    message: string;
  } | null>(null);
  const summaryMoneda = row.summary?.moneda ?? 'ARS';

  const parsed = parseMoney(value);
  const valid =
    parsed != null && parsed >= 0 && parsed <= 100 && Math.abs(parsed - original) >= 0.0001;

  // Live "what-if": does applying `parsed` as the replacement % land
  // this approval's line in usado territory? Server wraps the existing
  // `computeWouldBeUsadoAfterDiscountChange`. Debounced 300ms so the
  // admin's typing doesn't hammer the endpoint. AR / non-Nova MX
  // always return `wouldBeUsado=false` so the warning never fires
  // outside MX Nova flow.
  const [debouncedParsed, setDebouncedParsed] = useState<number | null>(null);
  useEffect(() => {
    if (parsed == null || parsed < 0 || parsed > 100) {
      setDebouncedParsed(null);
      return;
    }
    const t = setTimeout(() => setDebouncedParsed(parsed), 300);
    return () => clearTimeout(t);
  }, [parsed]);
  const usadoPreviewQ = useQuery({
    queryKey: ['rechazar-descuento-usado-preview', row.id, debouncedParsed],
    queryFn: () => previewDiscountUsado(Number(row.id), debouncedParsed!),
    enabled: debouncedParsed != null,
    staleTime: 0,
  });
  const replacementWouldBeUsado = usadoPreviewQ.data?.wouldBeUsado ?? false;

  const mutation = useMutation({
    mutationFn: () => {
      if (parsed == null) throw new Error('Valor inválido');
      return rechazarDescuento(row.id, parsed);
    },
    onSuccess: (r) => {
      if (r.outcome === 'rejected_replaced') {
        toast.success('Descuento reemplazado', {
          description: `Se aplicó ${r.nuevoDescuento}% en lugar del ${f.Descuento_AD}% solicitado.`,
        });
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
        qc.invalidateQueries({ queryKey: ['ventas'] });
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        qc.invalidateQueries({ queryKey: ['home', 'operaciones'] });
        onClose();
        return;
      }
      if (r.outcome === 'overpayment_risk') {
        // Stay open — surface the block inline so the admin can
        // immediately type a smaller % and retry.
        setBlocked({
          currentTotal: r.currentTotal,
          projectedTotal: r.projectedTotal,
          paidSum: r.paidSum,
          proposedDiscountPct: r.proposedDiscountPct,
          suggestedMaxPct: r.suggestedMaxPct,
          message: r.message,
        });
        return;
      }
      // blocked_status / blocked_same_value
      toast.info('Solicitud no aplicable', { description: r.message });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo rechazar el descuento', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });
  const processingMessage = 'Aplicando contraoferta…';

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && processingMessage}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>Rechazar y reemplazar descuento</DialogTitle>
              <DialogDescription>
                El vendedor solicitó un {f.Descuento_AD}% sobre{' '}
                <span className="font-medium text-foreground">{f.Articulo_AD}</span>.
                Ingresá el nuevo descuento a aplicar.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) mutation.mutate();
          }}
          className="flex flex-1 flex-col overflow-hidden"
          noValidate
        >
          <DialogBody className="space-y-2">
            {f.WasUsado_AD && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
                <p className="font-semibold uppercase tracking-wide text-rose-700">
                  ⚠ Usado
                </p>
                <p className="mt-1">
                  La solicitud cae en territorio usado: con el descuento
                  pedido la venta queda por debajo del costo contable +
                  comisión + IVA. Rechazar con reemplazo no
                  necesariamente saca a la venta del estado usado —
                  verificá que el nuevo porcentaje deje la venta por
                  encima del mínimo si querés evitarlo.
                </p>
              </div>
            )}
            <Label htmlFor="nuevo-descuento">Nuevo descuento (%)</Label>
            {/* Live preview note rendered below the input — see further down. */}
            <div className="relative">
              <Input
                id="nuevo-descuento"
                type="text"
                inputMode="decimal"
                variant="filled"
                autoFocus
                value={value}
                onChange={(e) => {
                  setValue(sanitizeDecimal(e.target.value));
                  // Editing dismisses the prior overpayment block —
                  // server re-validates on the next submit.
                  if (blocked) setBlocked(null);
                }}
                className="pr-10 text-right"
                placeholder="0"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Debe estar entre 0 y 100, y ser distinto al {f.Descuento_AD}% solicitado.
            </p>
            {valid && replacementWouldBeUsado && (
              <p className="text-xs leading-relaxed text-rose-700">
                ⚠ Con {parsed}% la venta queda igual por debajo del
                costo contable + comisión + IVA — seguirá marcada como
                usado.
              </p>
            )}
            {blocked && (
              <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    {blocked.proposedDiscountPct}% dejaría el total en{' '}
                    <span className="font-semibold">
                      {formatMoney(blocked.projectedTotal, summaryMoneda)}
                    </span>
                    , por debajo de lo cobrado (
                    <span className="font-semibold">
                      {formatMoney(blocked.paidSum, summaryMoneda)}
                    </span>
                    ).{' '}
                    {blocked.suggestedMaxPct != null
                      ? `Máximo permitido: ${blocked.suggestedMaxPct}%.`
                      : 'Incluso 0% deja la venta en sobre-pago — cancelá la solicitud desde la aprobación.'}
                  </span>
                </div>
                {blocked.suggestedMaxPct != null && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setValue(String(blocked.suggestedMaxPct));
                      setBlocked(null);
                    }}
                    className="h-7 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                  >
                    Usar {blocked.suggestedMaxPct}%
                  </Button>
                )}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!valid || mutation.isPending}>
              Reemplazar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cell helpers
// ---------------------------------------------------------------------------

function ActionCell({
  onApprove,
  onReject,
}: {
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={onApprove}
        aria-label="Aprobar"
        title="Aprobar"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Check className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onReject}
        aria-label="Rechazar"
        title="Rechazar"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive transition-colors hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-4 py-3 font-semibold', className)}>{children}</th>;
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td className={cn('px-4 py-3', className)} title={title}>
      {children}
    </td>
  );
}

function RowStatus({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted-foreground">
        <div className="flex items-center justify-center gap-3">{children}</div>
      </td>
    </tr>
  );
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-4 py-3.5">
              <Skeleton className="h-4 w-full max-w-[160px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Mobile-card shell shared by the 3 approval queues. Renders skeleton/
 * error/empty states uniformly so each table only ships its per-row
 * card markup.
 */
function MobileCardShell({
  loading,
  error,
  empty,
  emptyMessage,
  errorMessage = 'No se pudieron cargar las solicitudes.',
  children,
}: {
  loading: boolean;
  error: boolean;
  empty: boolean;
  emptyMessage: string;
  errorMessage?: string;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center text-sm text-destructive">{errorMessage}</div>
    );
  }
  if (empty) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</div>
    );
  }
  return <ul className="divide-y divide-border">{children}</ul>;
}

function sanitizeDecimal(value: string): string {
  const cleaned = value.replace(/[^\d.,]/g, '');
  const match = cleaned.match(/^\d*([.,]\d*)?/);
  return match ? match[0] : '';
}

// ---------------------------------------------------------------------------
// Facturación % — table + dialogs
// ---------------------------------------------------------------------------

function FacturaPorcentajeTable({
  rows,
  loading,
  error,
  searchActive,
  onApprove,
  onReject,
}: {
  rows: FacturaPorcentajeItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onApprove: (row: FacturaPorcentajeItem) => void;
  onReject: (row: FacturaPorcentajeItem) => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay solicitudes de porcentaje de facturación pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>Venta</Th>
              <Th>Cliente</Th>
              <Th className="text-right">% Solicitado</Th>
              <Th className="text-right">% Actual</Th>
              <Th>Solicitante</Th>
              <Th>Motivo</Th>
              <Th className="w-32 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={8} />
            ) : error ? (
              <RowStatus cols={8}>
                <span className="text-destructive">
                  No se pudieron cargar las solicitudes.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={8}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  className={cn(
                    'transition-colors',
                    r.wasUsado
                      ? 'bg-rose-50/60 hover:bg-rose-100/60'
                      : 'hover:bg-muted/30',
                  )}
                >
                  <Td className="text-muted-foreground">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </Td>
                  <Td className="text-foreground">
                    {r.venta?.sharepointId ?? '—'}
                  </Td>
                  <Td className="font-medium text-foreground">
                    <div className="inline-flex items-center gap-1.5">
                      <span>{r.venta?.cliente ?? '—'}</span>
                      {r.wasUsado && (
                        <span className="shrink-0 rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                          Usado
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right font-semibold text-amber-700">
                    {r.porcentajeSolicitado}%
                  </Td>
                  <Td className="text-right text-muted-foreground">
                    {r.porcentajeAnterior != null ? `${r.porcentajeAnterior}%` : '—'}
                  </Td>
                  <Td className="text-muted-foreground">
                    {r.solicitanteUsuarioApp ?? '—'}
                  </Td>
                  <Td
                    className="max-w-xs truncate text-muted-foreground"
                    title={r.motivo ?? undefined}
                  >
                    {r.motivo || '—'}
                  </Td>
                  <Td className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        title="Aprobar"
                        aria-label="Aprobar"
                        onClick={() => onApprove(r)}
                      >
                        <Check className="h-4 w-4 text-emerald-600" aria-hidden />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        title="Rechazar"
                        aria-label="Rechazar"
                        onClick={() => onReject(r)}
                      >
                        <X className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Mobile — single-column card layout. */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
        >
          {rows.map((r) => (
            <li key={r.id} className="space-y-1 px-4 py-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="truncate font-medium text-foreground">
                    {r.venta?.cliente ?? '—'}
                  </span>
                  {r.wasUsado && (
                    <span className="shrink-0 rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                      Usado
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  Venta {r.venta?.sharepointId ?? '—'}
                </div>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  Solicitado{' '}
                  <span className="font-semibold text-amber-700">
                    {r.porcentajeSolicitado}%
                  </span>{' '}
                  · Actual{' '}
                  {r.porcentajeAnterior != null
                    ? `${r.porcentajeAnterior}%`
                    : '—'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(r.createdAt).toLocaleDateString()}
                </div>
              </div>
              {r.motivo && (
                <div className="text-xs text-muted-foreground">{r.motivo}</div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => onReject(r)}>
                  Rechazar
                </Button>
                <Button size="sm" onClick={() => onApprove(r)}>
                  Aprobar
                </Button>
              </div>
            </li>
          ))}
        </MobileCardShell>
      </div>
    </div>
  );
}

/**
 * Compact "Solicitud de X% · Venta N — Cliente" line shared by the
 * Aprobar / Rechazar dialogs. Lives under the title as the
 * DialogDescription so the body can carry the actual call-to-action
 * (or replacement-% input) without cluttered preamble.
 */
function FacturaPctSubtitle({ row }: { row: FacturaPorcentajeItem }) {
  const venta = row.venta?.sharepointId ?? '—';
  const cliente = row.venta?.cliente ? ` — ${row.venta.cliente}` : '';
  return (
    <>
      Solicitud de{' '}
      <span className="font-semibold text-amber-700">
        {row.porcentajeSolicitado}%
      </span>{' '}
      · Venta {venta}
      {cliente}
    </>
  );
}

function AprobarFacturaPctDialog({
  row,
  onClose,
}: {
  row: FacturaPorcentajeItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => aprobarFacturaPorcentaje(row.id),
    onSuccess: (r) => {
      toast.success('Solicitud aprobada', {
        description: `Porcentaje aplicado: ${r.porcentajeAplicado}%`,
      });
      // Optimistic: drop the venta from every cached factura-pct-pending
      // list so the Pend.aprob badge on Ventas / Mantenimiento clears
      // instantly. The background invalidate that follows still does the
      // round-trip — this just hides the user-visible refetch latency.
      const spId = row.venta?.sharepointId;
      if (spId != null) {
        qc.setQueriesData<number[]>(
          { queryKey: ['ventas', 'factura-pct-pending'] },
          (prev) => (prev ? prev.filter((v) => v !== spId) : prev),
        );
      }
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      qc.invalidateQueries({ queryKey: ['factura-pct-auth'] });
      onClose();
    },
    onError: (err) => {
      toast.error('No se pudo aprobar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aprobar porcentaje de facturación</DialogTitle>
          <DialogDescription>
            <FacturaPctSubtitle row={row} />
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            El operador podrá guardar la pantalla de pagos con ese valor.
          </p>
          {row.wasUsado && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
              <p className="font-semibold uppercase tracking-wide text-rose-700">
                ⚠ Usado
              </p>
              <p className="mt-1">
                Con {row.porcentajeSolicitado}% el monto a facturar queda
                por debajo del costo contable + comisión + IVA. Si
                aprobás, la <strong>factura interna</strong> de esta
                venta saldrá marcada como usado: administración
                tendrá que cargar el monto manualmente al subir el PDF
                (el sistema no valida la coincidencia con el costo
                contable). El margen que se transfiere a Caja Diferencia
                depende de ese monto: si es igual al cobrado, no hay
                margen para transferir; si es menor, se transfiere la
                diferencia.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Aprobando…' : 'Aprobar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RechazarFacturaPctDialog({
  row,
  onClose,
}: {
  row: FacturaPorcentajeItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [replacementRaw, setReplacementRaw] = useState('');
  const replacement = useMemo(() => {
    if (replacementRaw.trim() === '') return null;
    const n = parseMoney(replacementRaw);
    return n != null && Number.isFinite(n) ? n : null;
  }, [replacementRaw]);
  // Supervisor override is intentionally NOT gated by the venta's
  // umbral — the whole point of "rechazar con reemplazo" is to let
  // them assign a value the operator couldn't pick directly. Only
  // bound by the percentage range itself.
  const validReplacement =
    replacement == null || (replacement >= 0 && replacement <= 100);
  // Fetch the venta's usado context so the dialog can warn live if
  // the admin's replacement % would itself land in usado territory.
  // Cheap: one read on dialog open, cached. Non-MX / non-Nova ventas
  // come back with `applies=false` and the warning never fires.
  const ventaSpIdStr = row.venta?.sharepointId
    ? String(row.venta.sharepointId)
    : null;
  const usadoCtxQ = useQuery({
    queryKey: ['rechazar-factpct-usado-ctx', ventaSpIdStr],
    queryFn: () => getUsadoContext(ventaSpIdStr!),
    enabled: ventaSpIdStr != null,
    staleTime: 30_000,
  });
  const usadoCtx = usadoCtxQ.data ?? null;
  // Live "would still be usado" predicate for the replacement value.
  // Mirrors the seller-side check: replacement × totalCotizacion / 100
  // is the projected totalFactura with the new porcentaje. Compare to
  // facturaMinimo from the usado context. Only meaningful when a
  // valid replacement was typed; null otherwise.
  const replacementWouldBeUsado = useMemo(() => {
    if (!usadoCtx?.applies) return false;
    if (replacement == null || replacement <= 0) return false;
    const totalCotizacion = row.venta?.totalCotizacion;
    if (totalCotizacion == null || totalCotizacion <= 0) return false;
    const projected = (replacement / 100) * totalCotizacion;
    return projected > 0 && projected < usadoCtx.facturaMinimo;
  }, [replacement, usadoCtx, row.venta?.totalCotizacion]);

  const mut = useMutation({
    mutationFn: () =>
      rechazarFacturaPorcentaje(row.id, replacement ?? undefined),
    onSuccess: (r) => {
      toast.success('Solicitud rechazada', {
        description:
          r.porcentajeAplicado != null
            ? `Porcentaje aplicado: ${r.porcentajeAplicado}%`
            : undefined,
      });
      const spId = row.venta?.sharepointId;
      if (spId != null) {
        qc.setQueriesData<number[]>(
          { queryKey: ['ventas', 'factura-pct-pending'] },
          (prev) => (prev ? prev.filter((v) => v !== spId) : prev),
        );
      }
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      qc.invalidateQueries({ queryKey: ['factura-pct-auth'] });
      onClose();
    },
    onError: (err) => {
      toast.error('No se pudo rechazar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rechazar porcentaje de facturación</DialogTitle>
          <DialogDescription>
            <FacturaPctSubtitle row={row} />
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Opcionalmente podés indicar un porcentaje de reemplazo (0%–100%).
            Si dejás el campo vacío, la venta vuelve al valor anterior (
            {row.porcentajeAnterior != null
              ? `${row.porcentajeAnterior}%`
              : 'sin valor previo'}
            ).
          </p>
          {row.wasUsado && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-900">
              <p className="font-semibold uppercase tracking-wide text-rose-700">
                ⚠ Usado
              </p>
              <p className="mt-1">
                La solicitud cae en territorio usado:{' '}
                {row.porcentajeSolicitado}% deja la venta por debajo del
                costo contable + comisión + IVA. Si rechazás con un
                reemplazo, fijate que el nuevo porcentaje deje la venta
                por encima del mínimo si querés sacarla del estado
                usado.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="factpct-reemplazo">% de reemplazo (opcional)</Label>
            <Input
              id="factpct-reemplazo"
              inputMode="decimal"
              placeholder={`p. ej. ${row.umbral}`}
              value={replacementRaw}
              onChange={(e) => setReplacementRaw(sanitizeDecimal(e.target.value))}
            />
            {!validReplacement && (
              <p className="text-xs text-destructive">
                El reemplazo debe estar entre 0% y 100%.
              </p>
            )}
            {validReplacement && replacementWouldBeUsado && usadoCtx && (
              <p className="text-xs leading-relaxed text-rose-700">
                ⚠ Con {replacement}% la venta queda igual por debajo del
                mínimo de costo + comisión + IVA (
                {formatMoney(usadoCtx.facturaMinimo, row.venta?.moneda ?? 'MXN')}
                ) — la venta seguirá marcada como usado.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !validReplacement}
          >
            {mut.isPending ? 'Rechazando…' : 'Rechazar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cambio de horno
// ---------------------------------------------------------------------------

function CambioHornoTable({
  rows,
  loading,
  error,
  searchActive,
  onApprove,
  onReject,
}: {
  rows: CambioHornoItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onApprove: (row: CambioHornoItem) => void;
  onReject: (row: CambioHornoItem) => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay cambios de horno pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>Venta</Th>
              <Th>Cliente</Th>
              <Th>Horno actual</Th>
              <Th>Horno nuevo</Th>
              <Th className="text-right">Diferencia</Th>
              <Th>Solicitante</Th>
              <Th className="w-32 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={8} />
            ) : error ? (
              <RowStatus cols={8}>
                <span className="text-destructive">
                  No se pudieron cargar las solicitudes.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={8}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => {
                const moneda = r.venta?.moneda ?? 'MXN';
                return (
                  <tr key={r.id} className="transition-colors hover:bg-muted/30">
                    <Td className="text-muted-foreground">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </Td>
                    <Td className="text-foreground">{r.venta?.sharepointId ?? '—'}</Td>
                    <Td className="font-medium text-foreground">
                      {r.venta?.cliente ?? '—'}
                    </Td>
                    <Td
                      className="max-w-[14rem] truncate text-muted-foreground"
                      title={r.hornoAnterior ?? undefined}
                    >
                      {r.hornoAnterior || '—'}
                      <span className="block text-xs">
                        {formatMoney(r.totalAnterior, moneda)}
                      </span>
                    </Td>
                    <Td
                      className="max-w-[14rem] truncate text-foreground"
                      title={r.hornoNuevo ?? undefined}
                    >
                      {r.hornoNuevo || '—'}
                      <span className="block text-xs text-muted-foreground">
                        {formatMoney(r.totalNuevo, moneda)}
                        {r.descuentoPct > 0 ? ` · ${r.descuentoPct}% desc.` : ''}
                      </span>
                    </Td>
                    <Td className="text-right font-semibold text-emerald-700">
                      +{formatMoney(r.delta, moneda)}
                    </Td>
                    <Td className="text-muted-foreground">
                      {r.solicitanteUsuarioApp ?? '—'}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          title="Aprobar"
                          aria-label="Aprobar"
                          onClick={() => onApprove(r)}
                        >
                          <Check className="h-4 w-4 text-emerald-600" aria-hidden />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          title="Rechazar"
                          aria-label="Rechazar"
                          onClick={() => onReject(r)}
                        >
                          <X className="h-4 w-4 text-destructive" aria-hidden />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Mobile */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
        >
          {rows.map((r) => {
            const moneda = r.venta?.moneda ?? 'MXN';
            return (
              <li key={r.id} className="space-y-1 px-4 py-3 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate font-medium text-foreground">
                    {r.venta?.cliente ?? '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Venta {r.venta?.sharepointId ?? '—'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.hornoAnterior || '—'} → {r.hornoNuevo || '—'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatMoney(r.totalAnterior, moneda)} →{' '}
                  {formatMoney(r.totalNuevo, moneda)} ·{' '}
                  <span className="font-semibold text-emerald-700">
                    +{formatMoney(r.delta, moneda)}
                  </span>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => onReject(r)}>
                    Rechazar
                  </Button>
                  <Button size="sm" onClick={() => onApprove(r)}>
                    Aprobar
                  </Button>
                </div>
              </li>
            );
          })}
        </MobileCardShell>
      </div>
    </div>
  );
}

function CambioHornoSubtitle({ row }: { row: CambioHornoItem }) {
  const venta = row.venta?.sharepointId ?? '—';
  const cliente = row.venta?.cliente ? ` — ${row.venta.cliente}` : '';
  return (
    <>
      {row.hornoAnterior || '—'} → <strong>{row.hornoNuevo || '—'}</strong> · Venta{' '}
      {venta}
      {cliente}
    </>
  );
}

function AprobarCambioHornoDialog({
  row,
  onClose,
}: {
  row: CambioHornoItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const moneda = row.venta?.moneda ?? 'MXN';
  const mut = useMutation({
    mutationFn: () => aprobarCambioHorno(row.id),
    onSuccess: (r) => {
      if (r.outcome === 'approved') {
        toast.success('Cambio de horno aprobado', {
          description:
            r.nextStatus === 'Pago Parcial'
              ? 'La venta volvió a Pago Parcial para cobrar la diferencia.'
              : 'La venta sigue en Pendiente Despacho (el pago ya cubre el total).',
        });
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
        qc.invalidateQueries({ queryKey: ['ventas'] });
        qc.invalidateQueries({ queryKey: ['mantenimiento'] });
        // The swap rewrote the line + header — drop the venta-detail cache too
        // so an open ventas detail page doesn't show the pre-swap horno/total.
        qc.invalidateQueries({ queryKey: ['detalle-venta'] });
        onClose();
      } else {
        // blocked_status / price_changed / stock_insuficiente / swap_failed
        toast.warning('No se pudo aprobar', { description: r.message });
        qc.invalidateQueries({ queryKey: ['aprobaciones'] });
        qc.invalidateQueries({ queryKey: ['mantenimiento'] });
      }
    },
    onError: (err) => {
      toast.error('No se pudo aprobar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aprobar cambio de horno</DialogTitle>
          <DialogDescription>
            <CambioHornoSubtitle row={row} />
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Total actual</span>
              <span className="tabular-nums">{formatMoney(row.totalAnterior, moneda)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">
                Total nuevo{row.descuentoPct > 0 ? ` (con ${row.descuentoPct}% desc.)` : ''}
              </span>
              <span className="tabular-nums font-medium">
                {formatMoney(row.totalNuevo, moneda)}
              </span>
            </div>
            <div className="flex justify-between gap-3 border-t pt-1">
              <span className="text-muted-foreground">Diferencia a cobrar</span>
              <span className="tabular-nums font-semibold text-emerald-700">
                +{formatMoney(row.delta, moneda)}
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Al aprobar se ajustan el detalle y los totales de la venta, se mueve el
            inventario (el horno anterior vuelve a disponible) y la venta pasa a{' '}
            <strong>Pago Parcial</strong> si queda saldo por cobrar.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Aprobando…' : 'Aprobar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RechazarCambioHornoDialog({
  row,
  onClose,
}: {
  row: CambioHornoItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => rechazarCambioHorno(row.id),
    onSuccess: () => {
      toast.success('Cambio de horno rechazado', {
        description: 'La venta no cambió.',
      });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['mantenimiento'] });
      onClose();
    },
    onError: (err) => {
      toast.error('No se pudo rechazar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rechazar cambio de horno</DialogTitle>
          <DialogDescription>
            <CambioHornoSubtitle row={row} />
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            La solicitud se rechaza y la venta queda tal cual está. Se puede volver a
            pedir el cambio desde Mantenimiento.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
          >
            {mut.isPending ? 'Rechazando…' : 'Rechazar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Facturación Previa (AR)
// ---------------------------------------------------------------------------

/**
 * Cola de solicitudes para emitir la factura ANTES de cobrar.
 *
 * La columna que importa es "Sin reserva": una promesa sobre una venta en
 * 'Aceptado' / 'Pendiente de Pago' no tiene stock reservado, así que la
 * factura saldría contra unidades que todavía se le pueden vender a otro.
 * No lo bloqueamos — se lo mostramos al aprobador para que decida.
 */
function FacturacionPreviaTable({
  rows,
  loading,
  error,
  searchActive,
  onApprove,
  onReject,
}: {
  rows: FacturacionPreviaItem[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onApprove: (row: FacturacionPreviaItem) => void;
  onReject: (row: FacturacionPreviaItem) => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay solicitudes de Factura Anticipada pendientes.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>Venta</Th>
              <Th>Cliente</Th>
              <Th>Medio de pago</Th>
              <Th className="text-right">Monto</Th>
              <Th>Estado del cobro</Th>
              <Th>Solicitante</Th>
              <Th className="w-32 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={8} />
            ) : error ? (
              <RowStatus cols={8}>
                <span className="text-destructive">
                  No se pudieron cargar las solicitudes.
                </span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={8}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  className={cn(
                    'transition-colors',
                    r.sinReservaStock
                      ? 'bg-amber-50/60 hover:bg-amber-100/60'
                      : 'hover:bg-muted/30',
                  )}
                >
                  <Td className="text-muted-foreground">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </Td>
                  <Td className="text-foreground">
                    {r.venta?.sharepointId ?? '—'}
                  </Td>
                  <Td className="font-medium text-foreground">
                    <div className="inline-flex items-center gap-1.5">
                      <span>{r.venta?.cliente ?? '—'}</span>
                      {r.sinReservaStock && (
                        <span
                          className="shrink-0 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                          title="La venta todavía no reservó stock"
                        >
                          Sin reserva
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-muted-foreground">
                    {etiquetaCaja(r.comprobante?.medioPago ?? '—')}
                  </Td>
                  {/* El importe CONGELADO al pedir (`montoSnapshot`), que es
                      exactamente lo que la guarda de deriva va a exigir al
                      emitir: aprobar es autorizar ESE número. Cae a
                      `pesos ?? monto` sólo en solicitudes viejas sin snapshot.
                      Va en moneda de la VENTA — mostrar `monto` con la
                      etiqueta de la venta subvaluaba un medio USD ~1500x
                      ("$ 1.000" por una emisión de $1.000.000). El nativo va
                      debajo. */}
                  <Td className="text-right font-semibold text-foreground">
                    {r.montoSnapshot ?? r.comprobante?.pesos ?? r.comprobante?.monto ?? null ? (
                      <>
                        {formatMoney(
                          r.montoSnapshot ?? r.comprobante?.pesos ?? r.comprobante?.monto ?? 0,
                          r.venta?.moneda ?? 'ARS',
                        )}
                        {r.comprobante?.pesos != null &&
                          r.comprobante?.monto != null &&
                          r.comprobante.pesos !== r.comprobante.monto && (
                            <div className="text-xs font-normal text-muted-foreground">
                              {formatMoney(r.comprobante.monto, 'USD')}
                            </div>
                          )}
                      </>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td className="text-muted-foreground">
                    {r.comprobante?.status === 'Pendiente de Cobro'
                      ? 'Promesa de pago'
                      : r.comprobante?.status ?? '—'}
                  </Td>
                  <Td className="text-muted-foreground">
                    {r.solicitanteUsuarioApp ?? '—'}
                  </Td>
                  <Td className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        title="Aprobar"
                        aria-label="Aprobar"
                        onClick={() => onApprove(r)}
                      >
                        <Check className="h-4 w-4 text-emerald-600" aria-hidden />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        title="Rechazar"
                        aria-label="Rechazar"
                        onClick={() => onReject(r)}
                      >
                        <X className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Mobile — single-column card layout. */}
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
        >
          {rows.map((r) => (
            <li key={r.id} className="space-y-1 px-4 py-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="truncate font-medium text-foreground">
                    {r.venta?.cliente ?? '—'}
                  </span>
                  {r.sinReservaStock && (
                    <span className="shrink-0 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                      Sin reserva
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  Venta {r.venta?.sharepointId ?? '—'}
                </div>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    {/* Mismo importe congelado que la tabla desktop. */}
                    {formatMoney(
                      r.montoSnapshot ?? r.comprobante?.pesos ?? r.comprobante?.monto ?? 0,
                      r.venta?.moneda ?? 'ARS',
                    )}
                  </span>{' '}
                  · {etiquetaCaja(r.comprobante?.medioPago ?? '—')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(r.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {r.comprobante?.status === 'Pendiente de Cobro'
                  ? 'Promesa de pago'
                  : r.comprobante?.status ?? '—'}
                {r.solicitanteUsuarioApp ? ` · ${r.solicitanteUsuarioApp}` : ''}
              </div>
              {r.motivo && (
                <div className="text-xs text-muted-foreground">{r.motivo}</div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => onReject(r)}>
                  Rechazar
                </Button>
                <Button size="sm" onClick={() => onApprove(r)}>
                  Aprobar
                </Button>
              </div>
            </li>
          ))}
        </MobileCardShell>
      </div>
    </div>
  );
}

/** Subtítulo compartido por los diálogos de Aprobar / Rechazar. */
function FacturacionPreviaSubtitle({ row }: { row: FacturacionPreviaItem }) {
  const venta = row.venta?.sharepointId ?? '—';
  const cliente = row.venta?.cliente ? ` — ${row.venta.cliente}` : '';
  // El importe congelado — el mismo que la guarda de deriva va a exigir al
  // emitir. Es el número que el aprobador está autorizando.
  const monto = formatMoney(
    row.montoSnapshot ?? row.comprobante?.pesos ?? row.comprobante?.monto ?? 0,
    row.venta?.moneda ?? 'ARS',
  );
  return (
    <>
      Cobro de <span className="font-semibold text-foreground">{monto}</span> ·
      Venta {venta}
      {cliente}
    </>
  );
}

function AprobarFacturacionPreviaDialog({
  row,
  onClose,
}: {
  row: FacturacionPreviaItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => aprobarFacturacionPrevia(row.id),
    onSuccess: () => {
      toast.success('Factura Anticipada aprobada', {
        description: 'El cobro ya aparece en Facturación para enviarlo a ARCA.',
      });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      qc.invalidateQueries({ queryKey: ['administracion'] });
      // Prefijo: alcanza tanto ['pagos', country, 'comprobantes', id] como
      // ['pagos','comprobantes-full', id] (que no lleva country). Es la única
      // key que renderiza el flag FacturacionPrevia y el techo de facturación.
      qc.invalidateQueries({ queryKey: ['pagos'] });
      onClose();
    },
    onError: (err) => {
      toast.error('No se pudo aprobar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });
  const esPromesa = row.comprobante?.status === 'Pendiente de Cobro';
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Aprobar Factura Anticipada</DialogTitle>
          <DialogDescription>
            <FacturacionPreviaSubtitle row={row} />
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            El cobro va a aparecer en <strong>Facturación</strong> listo para
            enviar a ARCA <strong>antes de recibir el pago</strong>. Después
            sigue su curso normal
            {esPromesa
              ? ' y queda en Cuentas por Cobrar hasta que se cobre.'
              : ' y queda en Cobros a Confirmar hasta que se confirme.'}
          </p>
          {row.motivo && (
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">Motivo: </span>
              {row.motivo}
            </div>
          )}
          {row.sinReservaStock && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <p className="font-semibold uppercase tracking-wide text-amber-800">
                ⚠ Sin reserva de stock
              </p>
              <p className="mt-1">
                La venta todavía no reservó unidades, así que la factura sale
                contra stock que se le puede vender a otro cliente. Una vez
                emitido el CAE el comprobante es real ante ARCA:{' '}
                <strong>sólo una Nota de Crédito lo revierte</strong>.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? 'Aprobando…' : 'Aprobar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RechazarFacturacionPreviaDialog({
  row,
  onClose,
}: {
  row: FacturacionPreviaItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const mut = useMutation({
    mutationFn: () => rechazarFacturacionPrevia(row.id, motivo.trim()),
    onSuccess: () => {
      toast.success('Solicitud rechazada', {
        description: 'El cobro sigue el flujo normal: se factura después de cobrarse.',
      });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      qc.invalidateQueries({ queryKey: ['ventas'] });
      // Simetría con aprobar: el rechazo NO escribe en el comprobante (sólo en
      // la autorización), así que la pantalla de Pagos no se entera por
      // realtime — el tag "Previa pendiente" se queda pegado y el botón de
      // volver a pedirla no reaparece.
      qc.invalidateQueries({ queryKey: ['pagos'] });
      onClose();
    },
    onError: (err) => {
      toast.error('No se pudo rechazar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });
  // El motivo es obligatorio: sin él el vendedor sólo ve el tag "Rechazada" y
  // vuelve a pedir la misma factura anticipada sin saber qué corregir.
  const motivoValido = motivo.trim().length >= 3;
  return (
    <Dialog open onOpenChange={(o) => !o && !mut.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rechazar Factura Anticipada</DialogTitle>
          <DialogDescription>
            <FacturacionPreviaSubtitle row={row} />
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-muted-foreground">
            No se modifica nada: el cobro sigue el flujo de siempre y se va a
            poder facturar recién cuando esté confirmado.
          </p>
          <div className="space-y-1">
            <Label htmlFor="motivo-rechazo-previa" className="text-sm">
              Motivo del rechazo
            </Label>
            <Textarea
              id="motivo-rechazo-previa"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={mut.isPending}
              rows={3}
              maxLength={500}
              placeholder="Por qué no se autoriza a facturar antes del cobro"
            />
            <p className="text-xs text-muted-foreground">
              Se lo va a ver el vendedor antes de decidir si vuelve a pedirla.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !motivoValido}
            title={motivoValido ? undefined : 'Escribí el motivo del rechazo'}
          >
            {mut.isPending ? 'Rechazando…' : 'Rechazar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** E-cheqs esperando autorización (AR). */
function EcheqsTable({
  rows,
  loading,
  error,
  searchActive,
  onApprove,
  onReject,
}: {
  rows: Echeq[];
  loading: boolean;
  error: boolean;
  searchActive: boolean;
  onApprove: (row: Echeq) => void;
  onReject: (row: Echeq) => void;
}) {
  const emptyMessage = searchActive
    ? 'Sin coincidencias para la búsqueda.'
    : 'No hay E-cheqs esperando autorización.';
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
      <div className="hidden min-h-0 flex-1 overflow-y-auto md:block">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Fecha</Th>
              <Th>Venta</Th>
              <Th>Cliente</Th>
              <Th>Cheque</Th>
              <Th>Fecha de pago</Th>
              <Th className="text-right">Monto</Th>
              <Th>Solicitante</Th>
              <Th className="w-32 text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <SkeletonRows cols={8} />
            ) : error ? (
              <RowStatus cols={8}>
                <span className="text-destructive">No se pudieron cargar los E-cheqs.</span>
              </RowStatus>
            ) : rows.length === 0 ? (
              <RowStatus cols={8}>{emptyMessage}</RowStatus>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="transition-colors hover:bg-muted/30">
                  <Td className="text-muted-foreground">{fechaAr(r.creadoAt)}</Td>
                  <Td className="text-foreground">{r.ventaId ?? '—'}</Td>
                  <Td className="font-medium text-foreground">{r.cliente ?? '—'}</Td>
                  <Td className="text-muted-foreground">
                    <div className="text-foreground">N° {r.nroCheque}</div>
                    <div className="text-xs">
                      {r.bancoEmisor} · CUIT {r.cuitLibrador}
                    </div>
                  </Td>
                  <Td className="text-muted-foreground">{fechaAr(r.fechaPago)}</Td>
                  <Td className="text-right font-semibold text-foreground">
                    {formatMoney(r.monto, 'ARS')}
                  </Td>
                  <Td className="text-muted-foreground">{r.solicitante ?? r.vendedor ?? '—'}</Td>
                  <Td className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        title="Autorizar"
                        aria-label="Autorizar"
                        onClick={() => onApprove(r)}
                      >
                        <Check className="h-4 w-4 text-emerald-600" aria-hidden />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        title="Rechazar"
                        aria-label="Rechazar"
                        onClick={() => onReject(r)}
                      >
                        <X className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto md:hidden">
        <MobileCardShell
          loading={loading}
          error={error}
          empty={rows.length === 0}
          emptyMessage={emptyMessage}
        >
          {rows.map((r) => (
            <li key={r.id} className="space-y-1 px-4 py-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate font-medium text-foreground">{r.cliente ?? '—'}</span>
                <span className="text-xs text-muted-foreground">Venta {r.ventaId ?? '—'}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{formatMoney(r.monto, 'ARS')}</span>
                {` · N° ${r.nroCheque} · ${r.bancoEmisor} · pago ${fechaAr(r.fechaPago)}`}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => onReject(r)}>
                  Rechazar
                </Button>
                <Button size="sm" onClick={() => onApprove(r)}>
                  Autorizar
                </Button>
              </div>
            </li>
          ))}
        </MobileCardShell>
      </div>
    </div>
  );
}
